import type { QueryClient } from '@tanstack/vue-query';
import { queryClient } from '@/plugins/vueQuery';
import { queryKeys } from './constants';
import { getTeamBudgetQueryKey } from './queries/budgets';
import { getTeamAthletesListQueryKey } from './queries/athletes';

const queryKeyFilters = {
  PAYMENTS: 'payments',
};

// Helper function to pass in multiple queries to invalidate
export const queriesToInvalidate = async (queryClient: QueryClient, queryKeys: readonly unknown[][]) => {
  await Promise.all(
    queryKeys.map((queryKey) =>
      queryClient.invalidateQueries({ queryKey }),
    ),
  );
};

/**
 * Invalidate payment related queries
 * @param teamTwrn Optional team TWRN to invalidate queries for a specific team
 * @param agreementTwrn Optional agreement TWRN to invalidate queries for a specific agreement
 *
 * This function will invalidate all payment related queries, if a teamTwrn is provided, it will only invalidate payments related to the team. If an agreementTwrn is provided, it will only invalidate payments related to the agreement. If both are provided, it will only invalidate payments related to the agreement.
 */
export const invalidatePaymentsQueries = (teamTwrn?: string | null, agreementTwrn?: string | null) => {
  return queryClient.invalidateQueries({
    predicate: (query) => {
      const { queryKey } = query;

      if (agreementTwrn && queryKey.includes(queryKeys.AGREEMENT_PAYMENTS)) {
        return queryKey.includes(agreementTwrn);
      }

      const firstQueryKey = queryKey[0];

      const shouldInvalidateQuery = typeof firstQueryKey === 'string' && firstQueryKey.startsWith(queryKeyFilters.PAYMENTS);


      if (teamTwrn) {
        return shouldInvalidateQuery && queryKey.includes(teamTwrn);
      }
      return shouldInvalidateQuery;
    },
  });
};

export const invalidateTeamPayments = (teamTwrn?: string | null) => {
  return queryClient.invalidateQueries({
    predicate: (query) => {
      const { queryKey } = query;
      const shouldInvalidateQuery = queryKey.includes(queryKeys.TEAM_PAYMENTS) || queryKey.includes(queryKeys.PAYMENT_REQUEST);
      if (teamTwrn) {
        return shouldInvalidateQuery && queryKey.includes(teamTwrn);
      }
      return shouldInvalidateQuery;
    },
  });
};

export const invalidateAgreementPayments = (teamTwrn?: string | null, agreementTwrn?: string | null) => {
  return queryClient.invalidateQueries({
    predicate: (query) => {
      const { queryKey } = query;
      const shouldInvalidateQuery = queryKey.includes(queryKeys.AGREEMENT_PAYMENTS) || queryKey.includes(queryKeys.PAYMENT_REQUEST);

      if (agreementTwrn) {
        return shouldInvalidateQuery && queryKey.includes(agreementTwrn);
      } else if (teamTwrn) {
        return shouldInvalidateQuery && queryKey.includes(teamTwrn);
      }

      return shouldInvalidateQuery;
    },
  });
};

export const invalidateAgreementsByContracts = (teamTwrn?: string | null) => {
  return queryClient.invalidateQueries({
    predicate: (query) => {
      const { queryKey } = query;
      const shouldInvalidateQuery = queryKey.includes(queryKeys.AGREEMENTS_BY_CONTRACT);

      return shouldInvalidateQuery && queryKey.includes(teamTwrn);
    },
  });
};

export const invalidateUserPermissions = (userTwrn?: string | null) => {
  return queryClient.invalidateQueries({
    predicate: (query) => {
      const { queryKey } = query;

      const isUserPermissionsQuery = queryKey.includes(queryKeys.USER_PERMISSIONS);
      const isUserPermissionsListQuery = queryKey.includes(queryKeys.USER_PERMISSIONS_LIST);

      if (!isUserPermissionsQuery && !isUserPermissionsListQuery) {
        return false;
      }

      // If no userTwrn filter, invalidate all matching queries
      if (!userTwrn) {
        return true;
      }

      // The real listing query is ['users', 'getUserPermissionsList'] with no userTwrn.
      // Always invalidate it since it shows all users.
      // Check if this is the listing query without any additional elements (userTwrn)
      const isListingWithoutUserTwrn = isUserPermissionsListQuery
        && queryKey.length === 2
        && queryKey[0] === 'users';

      if (isListingWithoutUserTwrn) {
        return true;
      }

      // For all other queries, only invalidate if the userTwrn matches
      return queryKey.includes(userTwrn);
    },
  });
};

export const invalidateTeamBudgetAthletes = (
  teamTwrn?: string | null,
  budgetTwrn?: string | null,
) => queriesToInvalidate(queryClient, [
  getTeamBudgetQueryKey(teamTwrn, budgetTwrn),
  getTeamAthletesListQueryKey(teamTwrn),
]);

// Query Key Helpers

export const makeMeQueryKey = () => ['users', queryKeys.ME];
export const makeUserPermissionsQueryKey = (userTwrn?: string | null) => userTwrn ? ['users', queryKeys.USER_PERMISSIONS, userTwrn] : ['users', queryKeys.USER_PERMISSIONS];
export const makeUserPermissionsListQueryKey = () => ['users', queryKeys.USER_PERMISSIONS_LIST];

export const makeAthleteEarningsQueryKey = (params: {
  teamTwrn: string | null;
  budgetTwrn: string;
  athleteTwrn: string;
}) => [queryKeys.ATHLETE_EARNINGS, params];

export const makeAthleteNotesQueryKey = (params: {
  teamTwrn: string | null;
  athleteTwrn: string;
}) => [queryKeys.ATHLETE_NOTES, params];

export const makeNotificationSettingsQueryKey = (userTwrn?: string | null) =>
  [queryKeys.NOTIFICATION_SETTINGS, userTwrn];
